import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../my-app/src/environments/environment';

const AGENT_API_URL = environment.URL;

@Injectable({
  providedIn: 'root',
})
export class ConfigService {

  private router = inject(Router);

  constructor(private http: HttpClient) { }

  /* =========================
     BASE URL HANDLER
     ========================= */
  private withBaseUrl(url: string): string {
    if (!url) return AGENT_API_URL;

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const base = AGENT_API_URL.endsWith('/')
      ? AGENT_API_URL.slice(0, -1)
      : AGENT_API_URL;

    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  }

  /* =========================
     DASHBOARD CONFIG
     ========================= */
  getDashboardConfig(configFileName: string): Observable<any> {
    return this.http.get(`/assets/dashboard-config/${configFileName}`);
  }

  /* =========================
     PAYLOAD HELPERS
     ========================= */
  private buildPayload(requestParamFilters: any): any {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    if (
      dashboardName === 'europe-impact-report' &&
      (!requestParamFilters || Object.keys(requestParamFilters).length === 0)
    ) {
      return {
        filter: {
          where: [],
          join: {},
          orderBy: {},
        },
      };
    }

    let staticFilters: any[] = [];
    const customerNameValue = requestParamFilters?.['customer_name'];

    if (dashboardName === 'customer-overview') {
      // added condition to filter out default customername in payload if customer name is selected 
      if (!customerNameValue && (!Array.isArray(customerNameValue) || customerNameValue.length > 0)) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    }else if(dashboardName === 'cspec-bridge'){
      if (!customerNameValue && (!Array.isArray(customerNameValue) || customerNameValue.length > 0)) {
        staticFilters = [
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }

    } else if (dashboardName !== 'opportunity-overview' && dashboardName !=='europe-impact-report') {
      if (!customerNameValue && (!Array.isArray(customerNameValue) || customerNameValue.length > 0)) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    }

    const dynamicFilters = Object.entries(requestParamFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value]; // recursively flatten the array to any depth eg. [[[]]].
        let updatedValues = rawValues.filter(v =>
          v !== null &&
          v !== undefined &&
          v !== '' &&
          !(Array.isArray(v) && v.length === 0)
        );



        if (updatedValues?.length === 0) return null;

        return {
          attributeName: key,
          operator: updatedValues.length > 1 ? 'IN' : '=',
          attributeValues: updatedValues,
          logicalOperator: 'AND'
        };
      })
      .filter((filter): filter is NonNullable<typeof filter> => filter !== null); //emoves null entries from the array.

    // Adjust logicalOperator handling
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else {
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = '';
      }
    }

    return {
      filter: {
        where: [...staticFilters, ...dynamicFilters],
        join: {},
        orderBy: {}
      }
    };
  }

  private buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();

    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (key === 'slider_range' && Array.isArray(value)) {
        params = params
          .set('startRange', value[0])
          .set('endRange', value[1]);
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(
          key,
          Array.isArray(value) ? value.join(',') : value
        );
      }
    }

    return params;
  }

  /* =========================
     GET CONFIG
     ========================= */
  getConfig(url: string, filters?: any, method = 'post', isTable = false): Observable<any> {
    const apiUrl = this.withBaseUrl(url);

    const useQueryParams = filters?.useQueryParams === true;
    if (filters?.useQueryParams !== undefined) {
      delete filters.useQueryParams;
    }

    const payload = this.buildPayload(filters?.requestParams || {});
    const queryParams = useQueryParams
      ? this.buildQueryParams(filters?.queryParams || {})
      : new HttpParams();

    return method === 'post'
      ? this.http.post<any>(apiUrl, payload, { params: queryParams })
      : this.http.get<any>(apiUrl, { params: queryParams });
  }

  /* =========================
     GET API
     ========================= */
 getApi(url: string, filters?: any, data?: any, method = 'post'): Observable<any> {
  const queryParamsForHttp = { ...(filters?.queryParams || {}) };
  const requestParamsForPayload = { ...(filters?.requestParams || {}) };
  const rawQueryString = filters?.rawQueryString;

  /* =========================
     EXCLUDE PAYLOAD PARAMS
     ========================= */
  if (Array.isArray(data?.excludePayloadParams) && data.excludePayloadParams.length > 0) {
    for (const key of data.excludePayloadParams) {
      delete requestParamsForPayload[key];
    }
  }

  /* =========================
     EMBED PATH PARAMS (NO ENCODING)
     ========================= */
  if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
    const embeddedParts: string[] = [];

    for (const paramName of data.embeddedParams) {
      const rawValue =
        queryParamsForHttp[paramName] ?? requestParamsForPayload[paramName];

      const normalizedValue = Array.isArray(rawValue)
        ? (rawValue.length === 1 ? rawValue[0] : rawValue.join(','))
        : rawValue;

      if (normalizedValue !== null && normalizedValue !== undefined && normalizedValue !== '') {
        const value = String(normalizedValue); // IMPORTANT: no encodeURIComponent

        if (url.includes(`{${paramName}}`)) {
          url = url.split(`{${paramName}}`).join(value);
        } else if (url.includes('{}')) {
          url = url.replace("{}", value);
        } else {
          embeddedParts.push(value);
        }

        delete queryParamsForHttp[paramName];
        delete requestParamsForPayload[paramName];
      }
    }

    if (embeddedParts.length > 0) {
      const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
      url = [baseUrl, ...embeddedParts].join('/');
    }
  }

  /* =========================
     BUILD PAYLOAD
     ========================= */
  let payload: any;

  if (data?.skipDefaultPayload) {
    if (data.customPayload) {
      const requestParams = { ...requestParamsForPayload };

      // Preserve workflow_id wrapping
      for (const key in requestParams) {
        if (key === 'workflow_id' && Array.isArray(requestParams[key])) {
          requestParams[key] = [[...requestParams[key]]];
        }
      }

      payload = { ...data.customPayload, ...requestParams };
    } else {
      const requestParams = { ...requestParamsForPayload };

      if (requestParams.filter) {
        const { filter, ...otherParams } = requestParams;
        const filterPayload = Array.isArray(filter) ? (filter[0] || {}) : filter;
        payload = { ...otherParams, ...filterPayload };
      } else {
        payload = requestParams;
      }

      if (Object.keys(payload).length === 0) {
        payload = null;
      }
    }
  } else {
    payload = {
      functionName: data?.functionName || '',
      version: data?.version || '1.0',
      requestData: {
        filter: this.createPayload(
          requestParamsForPayload,
          data?.entity || '',
          'filterA'
        )
      }
    };
  }

  /* =========================
     QUERY PARAMS
     ========================= */
  const useQueryParams = data?.useQueryParam !== false;
  let queryParams = new HttpParams();

  if (useQueryParams) {
    if (rawQueryString) {
      url = `${url}?${rawQueryString}`;
    } else {
      queryParams = this.buildQueryParams(queryParamsForHttp);
    }
  }

  /* =========================
     HEADERS
     ========================= */
  const httpOptions: { params?: HttpParams; headers?: HttpHeaders } = {
    params: queryParams
  };

  if (data?.headers) {
    let headers = new HttpHeaders();
    for (const key in data.headers) {
      if (Object.prototype.hasOwnProperty.call(data.headers, key)) {
        headers = headers.append(key, data.headers[key]);
      }
    }
    httpOptions.headers = headers;
  }

  /* =========================
     APPLY BASE URL (FIX)
     ========================= */
  const apiUrl = this.withBaseUrl(url);

  /* =========================
     HTTP CALL
     ========================= */
  const normalizedMethod = String(method || 'post').toLowerCase();

  if (normalizedMethod === 'get') {
    return this.http.get<any>(apiUrl, httpOptions);
  }

  return this.http.request<any>(normalizedMethod, apiUrl, {
    ...httpOptions,
    body: payload
  });
}




  /* =========================
     OTHER APIs
     ========================= */
  getDropdownOptions(url: string, payload: any): Observable<any> {
    return this.http.post(this.withBaseUrl(url), payload);
  }

  getData(url: string): Observable<any> {
    return this.http.get(this.withBaseUrl(url));
  }

  createPayload(filters: any = {}, entity = '', filterName = ''): any {
    return { entity, filterName };
  }
}
